import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Search, Grid, List, RefreshCw } from 'lucide-react';
import { Project } from '@/types/product-manager';
import { getProjects } from '@/services/productManagerService';
import { initializeLiveProjects } from '@/services/jlsAppsIntegration';
import LiveProjectCard from '@/components/product-manager/LiveProjectCard';
import CustomProjectCard from '@/components/product-manager/CustomProjectCard';
import CreateProjectDialog from '@/components/product-manager/CreateProjectDialog';

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const loadProjects = useCallback(async (autoSync = true) => {
    try {
      setLoading(true);
      
      // Auto-sync new apps from AVAILABLE_APPS on first load
      if (autoSync) {
        setSyncing(true);
        try {
          await initializeLiveProjects();
          console.log('✅ Auto-sync completed');
        } catch (syncError) {
          console.warn('⚠️ Auto-sync failed, continuing with existing projects:', syncError);
        } finally {
          setSyncing(false);
        }
      }
      
      const allProjects = await getProjects();
      setProjects(allProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleManualSync = useCallback(async () => {
    try {
      setSyncing(true);
      await initializeLiveProjects();
      await loadProjects(false); // Don't auto-sync again
      console.log('✅ Manual sync completed');
    } catch (error) {
      console.error('❌ Manual sync failed:', error);
    } finally {
      setSyncing(false);
    }
  }, [loadProjects]);

  useEffect(() => {
    loadProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredProjects = projects.filter(project => {
    // Add null checks to prevent undefined errors
    const projectName = project.name || '';
    const projectDescription = project.description || '';
    
    const matchesSearch = projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         projectDescription.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'live') return matchesSearch && project.type === 'live_app';
    if (activeTab === 'custom') return matchesSearch && project.type === 'custom';
    
    return matchesSearch;
  });

  const liveProjects = filteredProjects.filter(p => p.type === 'live_app');
  const customProjects = filteredProjects.filter(p => p.type === 'custom');

  const handleProjectCreated = () => {
    loadProjects();
    setShowCreateDialog(false);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Projects</h1>
            <p className="text-gray-600 dark:text-gray-400">Loading projects...</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Projects</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your JLS Apps features and custom projects
            {syncing && <span className="text-violet-600 ml-2">• Syncing new apps...</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleManualSync}
            variant="outline"
            disabled={syncing}
            className="border-violet-200 text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-950"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sync Apps
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-violet-600 hover:bg-violet-700">
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>


      {/* Project Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Projects ({filteredProjects.length})</TabsTrigger>
          <TabsTrigger value="live">Live Apps ({liveProjects.length})</TabsTrigger>
          <TabsTrigger value="custom">Custom ({customProjects.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          {filteredProjects.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm ? 'No projects match your search.' : 'No projects found.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
              : 'space-y-4'
            }>
              {/* Live Projects First */}
              {liveProjects.map(project => (
                <LiveProjectCard 
                  key={project.id} 
                  project={project} 
                  viewMode={viewMode}
                  onUpdate={loadProjects}
                />
              ))}
              
              {/* Custom Projects */}
              {customProjects.map(project => (
                <CustomProjectCard 
                  key={project.id} 
                  project={project} 
                  viewMode={viewMode}
                  onUpdate={loadProjects}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="live" className="mt-6">
          {liveProjects.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-gray-500 dark:text-gray-400">
                  No live apps match your search.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
              : 'space-y-4'
            }>
              {liveProjects.map(project => (
                <LiveProjectCard 
                  key={project.id} 
                  project={project} 
                  viewMode={viewMode}
                  onUpdate={loadProjects}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="custom" className="mt-6">
          {customProjects.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm ? 'No custom projects match your search.' : 'No custom projects yet.'}
                </p>
                <Button 
                  onClick={() => setShowCreateDialog(true)} 
                  className="mt-4 bg-violet-600 hover:bg-violet-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Project
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
              : 'space-y-4'
            }>
              {customProjects.map(project => (
                <CustomProjectCard 
                  key={project.id} 
                  project={project} 
                  viewMode={viewMode}
                  onUpdate={loadProjects}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onProjectCreated={handleProjectCreated}
      />
    </div>
  );
};

export default Projects;